import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { RecordContextProvider, useGetList } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { formatMoney } from '../cashbacks/formatMoney';
import {
  DestinationCell,
  MemberCell,
  SlaCell,
  StatusChip,
} from '../cashouts/CashoutList';

const formatDate = (iso: unknown): string =>
  typeof iso === 'string' ? new Date(iso).toLocaleDateString() : '';

/**
 * Recent activity for the active region: the latest cashout requests, read from
 * the same `cashouts` materialisation the Cashouts list uses (no new data
 * shape) and rendered with the SAME chip/cell components (member, destination,
 * status, SLA) wrapped per-row in a RecordContextProvider. Region-driven via the
 * standard `region` filter.
 */
export const RecentActivity = () => {
  const { activeRegion } = useRegion();
  const { data, isLoading } = useGetList('cashouts', {
    pagination: { page: 1, perPage: 8 },
    sort: { field: 'requestedAt', order: 'DESC' },
    filter: { region: activeRegion },
  });

  const rows = data ?? [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Recent activity
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Latest cashout requests in this region
        </Typography>

        <Box sx={{ mt: 1 }} aria-label="recent activity">
          {!isLoading && rows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No recent activity in this region.
            </Typography>
          )}
          {rows.map((row, i) => (
            <RecordContextProvider key={row.id} value={row}>
              <Box>
                {i > 0 && <Divider />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.25,
                  }}
                >
                  <Box sx={{ flex: '1 1 38%', minWidth: 0 }}>
                    <MemberCell />
                  </Box>
                  <Box sx={{ flex: '1 1 30%', minWidth: 0 }}>
                    <Typography variant="body2">
                      {formatMoney(row.amount, row.currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <DestinationCell />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: '0 0 auto',
                    }}
                  >
                    <StatusChip />
                    <SlaCell />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
                  >
                    {formatDate(row.requestedAt)}
                  </Typography>
                </Box>
              </Box>
            </RecordContextProvider>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
